//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import {
  ClientUserListResponse,
  IClientAttributionModelSettings,
  IGetElevationResponse,
  IInternalViewResponse,
  ISearchTrackingParamsRequest,
  ISimpleTrackingParam,
  ITimeZone,
} from '@apis/account-settings/accountSettingsApiModel';
import {
  ITrackingParams,
  ITrackingParamsResponse,
} from '@legacy-components/common/universal-image-actions/universal-image-actions-api-service';
import TimeZoneId from '@models/TimeZoneId';
import { isDefined } from '@utils/filterPredicates';
import {
  serializedGatewayFetch,
  serializedSessionFetch,
} from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const BASE_SETTINGS_URL = '/api/settings';
const USERS_URL = `/users`;
const TRACKING_PARAMS_URL = '/tracking-params';

function settingsFetch(endpoint: string, init?: RequestInit) {
  return serializedSessionFetch(`${BASE_SETTINGS_URL}${endpoint}`, init);
}

const ACCOUNTS_DEFAULT_ENDPOINT = '/api/v1';

const affableGatewayFetch = (
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'affable_gateway',
    `${ACCOUNTS_DEFAULT_ENDPOINT}${endpoint}`,
    init,
    useFetchHeader,
  );
};

/**
 * Changes the users password if oldPassword is correct
 *
 * PUT /api/settings/password
 */
export async function changeUserPassword(
  oldPassword: string,
  newPassword1: string,
  newPassword2: string,
): Promise<void> {
  const params = {
    oldPassword,
    newPassword1,
    newPassword2,
  };
  await settingsFetch(`${USERS_URL}/password`, {
    body: JSON.stringify(params),
    method: 'PUT',
  });
}

/**
 * Get the list of available timezones
 *
 * GET /api/time-zones/
 */
export async function getTimezones(): Promise<ITimeZone[]> {
  const res = await serializedSessionFetch('/api/time-zones');
  return await res.json();
}

/**
 * Update the users timezone
 *
 * PUT /api/settings/users/timezone
 */
export async function updateTimezone(timeZoneId: TimeZoneId): Promise<void> {
  const res = await settingsFetch(`${USERS_URL}/timezone`, {
    body: JSON.stringify({ timeZoneId }),
    method: 'PUT',
  });
}

/**
 * Get the users current view
 *
 * GET /api/settings/users/internal-view
 */
export async function getInternalView(): Promise<IInternalViewResponse> {
  const res = await settingsFetch(`${USERS_URL}/internal-view`);
  return await res.json();
}

/**
 * Sets the employee's view
 *
 * PUT /api/settings/users/internal-view
 */
export async function setInternalView(view: string): Promise<void> {
  await settingsFetch(`${USERS_URL}/internal-view`, {
    body: JSON.stringify({ view }),
    method: 'PUT',
  });
}

/**
 * Get user elevation settings
 *
 * GET /api/settings/users/elevate
 */
export async function getElevation(): Promise<IGetElevationResponse> {
  const res = await settingsFetch(`${USERS_URL}/elevate`);
  return await res.json();
}

/**
 * Elevates a user and returns the expiration
 *
 * POST /api/settings/users/elevate
 */
export async function elevateUser(
  reason: string,
  otherReason: string,
): Promise<IGetElevationResponse> {
  const params = {
    reason,
    otherReason,
  };
  const res = await settingsFetch(`${USERS_URL}/elevate`, {
    body: JSON.stringify(params),
    method: 'POST',
  });
  return await res.json();
}

/**
 * Revokes a users elevation
 *
 * DELETE /api/settings/users/elevate
 */
export async function revokeUserElevation(): Promise<void> {
  await settingsFetch(`${USERS_URL}/elevate`, {
    method: 'DELETE',
  });
}

/**
 * Save the tracking params
 *
 * PUT /api/settings/tracking-params
 */
export async function saveTrackingParams(
  params: ITrackingParams,
): Promise<void> {
  const res = await settingsFetch(`${TRACKING_PARAMS_URL}`, {
    body: JSON.stringify(params),
    method: 'PUT',
  });
  return await res.json();
}

/**
 * Get the clients tracking param settings
 *
 * GET /api/settings/tracking-params
 */
export async function getTrackingParamSettings(): Promise<ITrackingParamsResponse> {
  const res = await settingsFetch(`${TRACKING_PARAMS_URL}`);
  return await res.json();
}

/**
 * Get the recent tracking param defaults
 *
 * GET '/api/params/recent-default'
 */
export async function searchTrackingParams(
  requestParams: ISearchTrackingParamsRequest,
  init?: RequestInit,
): Promise<ISimpleTrackingParam[]> {
  const queryParams = $.param(requestParams);
  const res = await serializedSessionFetch(
    `/api/params/recent-default?${queryParams}`,
    isDefined(init) ? init : undefined,
  );

  return await res.json<ISimpleTrackingParam[]>();
}

/**
 * Get the clients ecom attribution settings
 *
 * GET /api/settings/clients/ecom-interaction
 */
export async function getClientAttributionModelSettings(): Promise<IClientAttributionModelSettings> {
  const res = await settingsFetch(`/clients/ecom-interaction`);
  return await res.json();
}

/**
 * Update the clients ecom attribution settings
 *
 * POST /api/settings/clients/ecom-interaction
 */
export async function updateClientAttributionModelSettings(
  attributionModel?: string,
): Promise<void> {
  const params = {
    setting: attributionModel,
  };
  const res = await settingsFetch(`/clients/ecom-interaction`, {
    body: JSON.stringify(params),
    method: 'POST',
  });
  return await res.json();
}

/**
 * Get the clients users list
 *
 * GET /api/v1/accounts/users
 */
export const getClientUsers = async (
  init?: RequestInit,
): Promise<ClientUserListResponse> => {
  // TODO: PD-284617 - Move the get client users' endoint to the webgateway
  const res = await affableGatewayFetch(`/accounts/users`, {
    ...init,
    method: 'GET',
  });
  const clientUserListResponse = await res.json<{
    data: ClientUserListResponse;
  }>();

  return clientUserListResponse.data;
};
